import {
	ErrorModal,
	useContextMenuMESF,
	useHasPermission,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	Grid2,
	Paper,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {
	CellKeyDownEvent,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	MILL_1,
	MILL_2,
	type MillConfig,
	type MillId,
} from "@/setup/mill-config/mill-config";

import { CustomStatusPanel } from "@/controls/ag-grid/components/CustomStatusPanel";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { DeleteEntity } from "@/shared/components/DeleteEntity";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { Group, Panel } from "react-resizable-panels";
import { useNavigate } from "react-router-dom";
import type { Section } from "../../../models/Section";
import {
	deleteSection,
	getSectionsExport,
} from "../../../repositories/SectionRepository";
import { useSectionOptionsFunctions } from "../hooks/useSectionOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { SectionDetails } from "./SectionDetails";
import type { SectionsExportData } from "../models/SectionExport";
import {
	exportSectionsWorkbook,
	type SectionExportGridApis,
} from "./section-export/exportSectionsWorkbook";
import { SectionExportGrids } from "./section-export/SectionExportGrids";
import { SectionReferenceTabs } from "./SectionReferenceTabs";
import { useSections } from "@/shared/selectors/SectionsOfPartSelector";
import {
	SECTION_ASSET_LABEL,
	type SectionTableRow,
} from "../models/SectionTableRow";

const SECTION_ASSET_FILTER = {
	ALL: "all",
	MILL_1: MILL_1.id,
	MILL_2: MILL_2.id,
} as const;

type SectionAssetFilter =
	(typeof SECTION_ASSET_FILTER)[keyof typeof SECTION_ASSET_FILTER];

interface SectionAssetFilterOption {
	label: string;
	value: SectionAssetFilter;
}

const SECTION_ASSET_FILTER_OPTIONS: SectionAssetFilterOption[] = [
	{ label: "Mill #1", value: SECTION_ASSET_FILTER.MILL_1 },
	{ label: "Mill #2", value: SECTION_ASSET_FILTER.MILL_2 },
	{ label: "All", value: SECTION_ASSET_FILTER.ALL },
];

const EMPTY_SECTIONS: Section[] = [];
const SECTION_PANEL_STORAGE_KEY = "section-table-panel-sizes";
const SECTION_PANEL_DEFAULT_LAYOUT = { table: 40, details: 60 };

const hasSavedSectionPanelLayout = () => {
	try {
		return localStorage.getItem(SECTION_PANEL_STORAGE_KEY) !== null;
	} catch {
		return true;
	}
};

const resolveDefaultAssetFilter = (
	defaultAreaId: string | number | null | undefined,
): SectionAssetFilter => {
	const assetId = Number(defaultAreaId);
	if (assetId === MILL_1.id) return SECTION_ASSET_FILTER.MILL_1;
	if (assetId === MILL_2.id) return SECTION_ASSET_FILTER.MILL_2;
	return SECTION_ASSET_FILTER.ALL;
};

const resolveDefaultMillId = (
	defaultAreaId: string | number | null | undefined,
): MillId => (Number(defaultAreaId) === MILL_1.id ? MILL_1.id : MILL_2.id);

interface ModalHideParams {
	shouldUpdate: boolean;
	close: boolean;
	entityId?: number;
}

export const TableSection = ({
	sectionId,
}: {
	sectionId: string | undefined;
}) => {
	const navigate = useNavigate();
	const hasPermissionTo = useHasPermission();
	const theme = useTheme();
	const isNarrow = useMediaQuery(theme.breakpoints.down("md"));
	const panelOrientation = isNarrow ? "vertical" : "horizontal";
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const defaultMillId = resolveDefaultMillId(defaultAreaId);

	const [filterValue, setFilterValue] = useState("");
	const [explicitAssetFilter, setExplicitAssetFilter] =
		useState<SectionAssetFilter | null>(null);
	const assetFilter =
		explicitAssetFilter ?? resolveDefaultAssetFilter(defaultAreaId);
	const [selectedRowData, setSelectedRowData] = useState<Section | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [exportGridApis, setExportGridApis] =
		useState<SectionExportGridApis | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [detailsMillId, setDetailsMillId] = useState<MillId>(defaultMillId);
	const [error, setError] = useState("");
	const skipSelectionGuardRef = useRef(false);

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels(SECTION_PANEL_STORAGE_KEY, {
		resetLayout: SECTION_PANEL_DEFAULT_LAYOUT,
	});
	const initialPanelLayout = hasSavedSectionPanelLayout()
		? getSavedPanelLayout()
		: SECTION_PANEL_DEFAULT_LAYOUT;

	const mill1SectionsQuery = useSections({
		assetId: MILL_1.id,
		onError: (error) => setError(error.message),
	});
	const mill2SectionsQuery = useSections({
		assetId: MILL_2.id,
		onError: (error) => setError(error.message),
	});

	const mill1Sections = mill1SectionsQuery.data?.sections ?? EMPTY_SECTIONS;
	const mill2Sections = mill2SectionsQuery.data?.sections ?? EMPTY_SECTIONS;
	const mill1SectionsById = useMemo(
		() =>
			new Map<number | null, Section>(
				mill1Sections.map((section) => [section.SectionId, section]),
			),
		[mill1Sections],
	);
	const mill2SectionsById = useMemo(
		() =>
			new Map<number | null, Section>(
				mill2Sections.map((section) => [section.SectionId, section]),
			),
		[mill2Sections],
	);
	const rows = useMemo<SectionTableRow[]>(() => {
		const allSectionIds = new Set([
			...mill1SectionsById.keys(),
			...mill2SectionsById.keys(),
		]);
		const sectionIds =
			assetFilter === SECTION_ASSET_FILTER.MILL_1
				? mill1SectionsById.keys()
				: assetFilter === SECTION_ASSET_FILTER.MILL_2
					? mill2SectionsById.keys()
					: allSectionIds.keys();

		return Array.from(sectionIds)
			.map((sectionId) => {
				const mill1Section = mill1SectionsById.get(sectionId);
				const mill2Section = mill2SectionsById.get(sectionId);
				const millIds: MillId[] = [
					...(mill1Section ? [MILL_1.id] : []),
					...(mill2Section ? [MILL_2.id] : []),
				];
				const sourceAssetId: MillId =
					assetFilter === SECTION_ASSET_FILTER.MILL_1
						? MILL_1.id
						: assetFilter === SECTION_ASSET_FILTER.MILL_2
							? MILL_2.id
							: millIds.includes(defaultMillId)
								? defaultMillId
								: (millIds[0] ?? defaultMillId);
				const section =
					sourceAssetId === MILL_1.id ? mill1Section : mill2Section;
				if (!section) return null;

				return {
					...section,
					HasProductionEvents: Boolean(
						mill1Section?.HasProductionEvents ||
							mill2Section?.HasProductionEvents,
					),
					Asset:
						millIds.length === 2
							? SECTION_ASSET_LABEL.SHARED
							: sourceAssetId === MILL_1.id
								? SECTION_ASSET_LABEL.MILL_1
								: SECTION_ASSET_LABEL.MILL_2,
					MillIds: millIds,
					SourceAssetId: sourceAssetId,
				};
			})
			.filter((section): section is SectionTableRow => section !== null)
			.sort(
				(a, b) =>
					Number(b.HasProductionEvents) - Number(a.HasProductionEvents) ||
					a.SectionName.localeCompare(b.SectionName),
			);
	}, [assetFilter, defaultMillId, mill1SectionsById, mill2SectionsById]);
	const isLoading =
		mill1SectionsQuery.isLoading || mill2SectionsQuery.isLoading;
	const selectedSectionId = selectedRowData?.SectionId;
	const sectionByMill: Partial<Record<MillId, Section>> = {
		[MILL_1.id]: mill1SectionsById.get(selectedSectionId ?? null),
		[MILL_2.id]: mill2SectionsById.get(selectedSectionId ?? null),
	};
	const availableMills: MillConfig[] = [MILL_1, MILL_2].filter(
		(mill) => sectionByMill[mill.id] !== undefined,
	);
	const selectedFilterMillId =
		assetFilter === SECTION_ASSET_FILTER.ALL ? null : assetFilter;
	const preferredMillId = selectedFilterMillId ?? defaultMillId;
	const resolvedDetailsMillId = sectionByMill[detailsMillId]
		? detailsMillId
		: sectionByMill[preferredMillId]
			? preferredMillId
			: (availableMills[0]?.id ?? preferredMillId);
	const detailsSection =
		sectionByMill[resolvedDetailsMillId] ?? selectedRowData;
	const showMillTabs = showModal === "update" && availableMills.length === 2;
	const canEditSpecifications = hasPermissionTo(
		"ui.navbar.products.section.specs",
	);
	const selectedAssetFilterOption =
		SECTION_ASSET_FILTER_OPTIONS.find(
			(option) => option.value === assetFilter,
		) ?? SECTION_ASSET_FILTER_OPTIONS[2];

	const resolveSectionDetailsMill = (section: Section): MillId => {
		const millIds =
			"MillIds" in section
				? (section as SectionTableRow).MillIds
				: [
						...(mill1SectionsById.has(section.SectionId) ? [MILL_1.id] : []),
						...(mill2SectionsById.has(section.SectionId) ? [MILL_2.id] : []),
					];

		if (millIds.includes(preferredMillId)) return preferredMillId;
		return millIds[0] ?? preferredMillId;
	};

	const selectSection = (
		section: Section,
		modal: "update" | "delete" = "update",
		shouldNavigate = true,
	) => {
		const selectedNode = gridApi?.getSelectedNodes()[0];
		if (selectedNode?.data?.SectionId !== section.SectionId) {
			skipSelectionGuardRef.current = true;
			gridApi?.getRowNode(String(section.SectionId))?.setSelected(true, true);
			skipSelectionGuardRef.current = false;
		}
		setSelectedRowData(section);
		setShowModal(modal);
		if (section.SectionId !== selectedSectionId) {
			setDetailsMillId(resolveSectionDetailsMill(section));
		}
		if (shouldNavigate) {
			navigate(`/configuration/sections/${section.SectionId}`);
		}
	};

	const applyModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: ModalHideParams) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleNewSection = () => {
		gridApi?.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		setDetailsMillId(preferredMillId);
		navigate("/configuration/sections/new");
	};

	const handleAssetFilterChange = (nextFilter: SectionAssetFilter) => {
		setExplicitAssetFilter(nextFilter);

		if (
			selectedSectionId !== null &&
			selectedSectionId !== undefined &&
			nextFilter !== SECTION_ASSET_FILTER.ALL &&
			!(nextFilter === MILL_1.id ? mill1SectionsById : mill2SectionsById).has(
				selectedSectionId,
			)
		) {
			gridApi?.deselectAll();
			setSelectedRowData(null);
			setShowModal("");
			navigate("/configuration/sections");
		}
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useSectionOptionsFunctions({
		onSelectSection: (section, modal) => selectSection(section, modal, false),
		handleNewSection,
	});
	const getMenuOptionsRef = useRef(getMenuOptions);
	useEffect(() => {
		getMenuOptionsRef.current = getMenuOptions;
	}, [getMenuOptions]);
	const { columnDefs } = useTableData({
		onSelectSection: (section, modal) => selectSection(section, modal),
		showContextMenu,
		showSectionFamily: assetFilter !== SECTION_ASSET_FILTER.MILL_1,
	});

	const { defaultColDef } = useGridDefinitions({
		OnEdit: (data: any) => {
			selectSection(data as Section);
		},
	});

	const onRowSelected = (event: RowSelectedEvent) => {
		if (skipSelectionGuardRef.current) return;
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.node.isSelected()) {
			const section = event.data as Section;
			if (section.SectionId === selectedSectionId) return;
			selectSection(section);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "sectionContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[showContextMenu],
	);

	const onCellKeyDown = (event: CellKeyDownEvent) => {
		const { api, node, event: keyEvent } = event;
		if (!keyEvent) return;
		const key = (keyEvent as KeyboardEvent).key;

		if (key === "ArrowUp" || key === "ArrowDown") {
			keyEvent.preventDefault();
			const currentIndex = node.rowIndex !== null ? node.rowIndex : -1;
			const nextRowIndex =
				key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1;
			const nextNode = api.getDisplayedRowAtIndex(nextRowIndex);

			if (nextNode) {
				api.ensureIndexVisible(nextRowIndex);
				selectSection(nextNode.data as Section);
			}
		}
	};

	const onModalHide = (params: ModalHideParams) => {
		applyModalHide(params);
	};

	const handleExportToExcel = async () => {
		if (!exportGridApis) {
			setError("Unable to export data. Please try again.");
			return;
		}

		let exportData: SectionsExportData;
		try {
			exportData = await getSectionsExport();
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
			return;
		}

		const now = new Date();
		const year = now.getFullYear();
		const month = String(now.getMonth() + 1).padStart(2, "0");
		const day = String(now.getDate()).padStart(2, "0");
		const formattedDate = `${year}-${month}-${day}`;

		exportSectionsWorkbook({
			apis: exportGridApis,
			data: exportData,
			fileName: `sections_${formattedDate}.xlsx`,
		});
	};

	useEffect(() => {
		if (!isLoading) {
			if (sectionId) {
				if (sectionId === "new") {
					if (showModal !== "create") handleNewSection();
				} else {
					const sectionNode = gridApi?.getRowNode(sectionId);
					const section =
						sectionNode?.data ??
						mill1SectionsById.get(Number(sectionId)) ??
						mill2SectionsById.get(Number(sectionId));

					if (sectionNode) {
						gridApi?.ensureNodeVisible(sectionNode);
					}
					if (section) selectSection(section, "update", false);
				}
			} else if (showModal) {
				applyModalHide({ shouldUpdate: false, close: true });
			}
		}
	}, [isLoading, gridApi, sectionId]);

	useEffect(() => {
		registerConfig({
			id: "sectionContext",
			getOptions: (data) => getMenuOptionsRef.current(data),
		});
	}, [registerConfig]);

	return (
		<>
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Sections
					</Typography>
				</Grid2>
				<Grid2 container size={{ md: 12 }} spacing={1} alignItems="center">
					<Grid2 size={{ xs: 12, md: 3 }}>
						<Autocomplete
							disableClearable
							options={SECTION_ASSET_FILTER_OPTIONS}
							value={selectedAssetFilterOption}
							getOptionLabel={(option) => option.label}
							isOptionEqualToValue={(option, value) =>
								option.value === value.value
							}
							onChange={(_, option) => handleAssetFilterChange(option.value)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Asset"
									inputProps={{
										...params.inputProps,
										"data-test-id": "section-asset-filter",
									}}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 8 }}>
						<TextFieldSearchFilter
							value={filterValue}
							onChange={(val) => {
								setFilterValue(val);
								gridApi?.setGridOption("quickFilterText", val);
							}}
						/>
					</Grid2>
					<Grid2
						size={{ xs: 12, md: 1 }}
						justifyContent="flex-end"
						display="flex"
					>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={!rows || rows.length === 0}
						/>
					</Grid2>
				</Grid2>
				<Grid2
					sx={{
						width: "100%",
						height: { xs: "180vh", md: "calc(100vh - 250px)" },
					}}
				>
					<Group
						key={panelOrientation}
						orientation={panelOrientation}
						defaultLayout={initialPanelLayout}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{
							height: isNarrow ? "100%" : "calc(100% + 24px)",
						}}
					>
						<Panel id="table" minSize={20}>
							<Paper elevation={2} style={{ height: "100%", width: "100%" }}>
								<AgGridReact<Section>
									loading={isLoading}
									rowData={rows}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows
									getContextMenuItems={getContextMenuItems}
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) =>
										String((params.data as Section).SectionId)
									}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									onCellKeyDown={onCellKeyDown}
									gridOptions={{
										rowSelection: {
											mode: "singleRow",
											checkboxes: false,
											enableClickSelection: true,
										},
										statusBar: {
											statusPanels: [
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
												{
													statusPanel: (params: IStatusPanelParams) => (
														<CustomStatusPanel handleNew={handleNewSection} />
													),
													align: "left",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator
							onDoubleClick={handleSeparatorDoubleClick}
							orientation={panelOrientation}
						/>
						<Panel id="details" minSize={20}>
							<Paper
								elevation={2}
								sx={{
									display: "flex",
									flexDirection: "column",
									height: "100%",
									overflow: "hidden",
									width: "100%",
								}}
							>
								<Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
									<SectionDetails
										section={detailsSection}
										assetId={resolvedDetailsMillId}
										availableMills={availableMills}
										showMillTabs={showMillTabs}
										onMillChange={setDetailsMillId}
										onHide={onModalHide}
										modal={showModal}
									>
										{detailsSection !== null &&
											detailsSection.SectionId !== null && (
												<SectionReferenceTabs
													key={resolvedDetailsMillId}
													section={detailsSection}
													sectionId={detailsSection.SectionId}
													assetId={resolvedDetailsMillId}
													disabled={showModal === "delete"}
													showFlowSheet={resolvedDetailsMillId === MILL_2.id}
													showSpecifications={
														showModal === "update" && canEditSpecifications
													}
													onError={(caughtError) =>
														setError(caughtError.message)
													}
												/>
											)}
									</SectionDetails>
								</Box>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<SectionExportGrids onGridsReady={setExportGridApis} />
			<DeleteEntity
				show={showModal === "delete"}
				entityParams={{
					Id: selectedRowData?.SectionId!,
					Name: selectedRowData?.SectionName!,
					Entity: "section",
				}}
				onHide={(update: boolean) => {
					onModalHide({ shouldUpdate: update, close: true });
					setShowModal(update ? "" : "update");
				}}
				deleteFunction={(sectionId: string) =>
					deleteSection({
						sectionId: Number(sectionId),
						defaultAreaId: resolvedDetailsMillId,
					})
				}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
